import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { Ref, type InputHTMLAttributes } from 'react';

import { HeroIcon } from '@/components/atoms/Icon';

const inputVariants = cva(
  'flex items-center self-stretch shadow-xs rounded-(--radius-sm) px-(--horizontal-spacing-2) py-(--vertical-spacing-2-5) gap-(--horizontal-spacing-2) bg-(--color-surface-default) text-(--color-text-primary) font-(family-name:--font-body) text-(length:--text-body-md) font-(--font-weight-body-md) border focus:ring transition',
  {
    variants: {
      variant: {
        default: 'border-(--color-border-neutral-softer-3)',
        error: 'border-(--color-border-error)',
      },
      size: {
        sm: 'text-sm py-1 px-2',
        md: 'text-base py-2 px-3',
        lg: 'text-lg py-3 px-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface InputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  ref?: Ref<HTMLInputElement>;
}

const Input = ({ variant, size, className, ref, ...props }: InputProps) => {
  const t = useTranslations();
  return (
    <div className="relative">
      <input
        type="text"
        className={inputVariants({ variant, size, className })}
        aria-invalid={variant === 'error'}
        ref={ref}
        required={props.required}
        aria-required={props.required}
        {...props}
      />
      {variant === 'error' && (
        <span
          className="absolute right-3 top-1/2 transform -translate-y-1/2"
          role="img"
          aria-label={t('ERROR_ICON')}
        >
          <HeroIcon
            icon={ExclamationCircleIcon}
            className="text-(--color-brand-primary-500)"
          />
        </span>
      )}
    </div>
  );
};

export { Input };
